import React, { useState } from 'react';
import { Download, Copy, Check, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const POSTING_TIPS = {
  instagram: {
    title: 'Instagram Reels',
    steps: [
      'Open Instagram → tap + → Reel',
      'Upload your downloaded video',
      'Paste your caption & hashtags',
      'Add music from Instagram library (optional)',
      'Set cover image and post',
    ],
  },
  tiktok: {
    title: 'TikTok',
    steps: [
      'Open TikTok → tap + → Upload',
      'Select your downloaded video',
      'Paste caption and add hashtags',
      'Choose a sound or keep original audio',
      'Set privacy and post',
    ],
  },
  youtube: {
    title: 'YouTube Shorts / Video',
    steps: [
      'Go to YouTube Studio → Create → Upload',
      'Upload your downloaded video',
      'Paste title & description',
      'Add tags from your hashtags',
      'Set visibility (Public/Unlisted/Private) and publish',
    ],
  },
};

export default function ExportPublishPanel({ title, description, hashtags, downloadUrl, downloadLabel = 'Download', className = '' }) {
  const [copied, setCopied] = useState(null);
  const [tipsOpen, setTipsOpen] = useState(false);

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(null), 2000);
    }).catch(() => toast.error('Copy failed'));
  };

  const caption = [description, hashtags ? (Array.isArray(hashtags) ? hashtags.map(t => `#${t.replace(/^#/, '')}`).join(' ') : hashtags) : ''].filter(Boolean).join('\n\n');
  const hashtagStr = hashtags ? (Array.isArray(hashtags) ? hashtags.map(t => `#${t.replace(/^#/, '')}`).join(' ') : hashtags) : '';

  return (
    <div className={`bg-zinc-900/60 border border-zinc-800/50 rounded-xl p-4 space-y-3 ${className}`} data-testid="export-publish-panel">
      {/* Download */}
      {downloadUrl && (
        <Button
          onClick={() => window.open(downloadUrl, '_blank')}
          className="w-full bg-primary/20 hover:bg-primary/30 text-primary"
          data-testid="export-download-btn"
        >
          <Download className="w-4 h-4 mr-2" /> {downloadLabel}
        </Button>
      )}

      {/* Copy Actions */}
      <div className="flex gap-2">
        {title && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => copyText(title, 'Title')}
            className="flex-1 text-xs h-8 border-zinc-700 hover:bg-zinc-800"
            data-testid="copy-title-btn"
          >
            {copied === 'Title' ? <Check className="w-3 h-3 mr-1 text-green-400" /> : <Copy className="w-3 h-3 mr-1" />}
            Copy Title
          </Button>
        )}
        {caption && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => copyText(caption, 'Caption')}
            className="flex-1 text-xs h-8 border-zinc-700 hover:bg-zinc-800"
            data-testid="copy-caption-btn"
          >
            {copied === 'Caption' ? <Check className="w-3 h-3 mr-1 text-green-400" /> : <Copy className="w-3 h-3 mr-1" />}
            Copy Caption & Hashtags
          </Button>
        )}
      </div>
      {hashtagStr && !caption && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => copyText(hashtagStr, 'Hashtags')}
          className="w-full text-xs h-8 border-zinc-700 hover:bg-zinc-800"
          data-testid="copy-hashtags-btn"
        >
          {copied === 'Hashtags' ? <Check className="w-3 h-3 mr-1 text-green-400" /> : <Copy className="w-3 h-3 mr-1" />}
          Copy Hashtags
        </Button>
      )}

      {/* Posting Checklist */}
      <button
        onClick={() => setTipsOpen(!tipsOpen)}
        className="w-full flex items-center justify-between text-xs text-zinc-500 hover:text-zinc-300 py-1.5 transition-colors"
        data-testid="posting-tips-toggle"
      >
        <span>Posting checklist</span>
        {tipsOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {tipsOpen && (
        <div className="space-y-3 animate-in fade-in duration-200" data-testid="posting-tips-content">
          {Object.values(POSTING_TIPS).map((platform) => (
            <div key={platform.title} className="bg-zinc-950/50 rounded-lg p-3">
              <p className="text-xs font-medium text-zinc-300 mb-1.5">{platform.title}</p>
              <ol className="space-y-0.5">
                {platform.steps.map((step, i) => (
                  <li key={i} className="text-[11px] text-zinc-500 flex gap-1.5">
                    <span className="text-zinc-600 shrink-0">{i + 1}.</span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
